import { MarkerInfo } from './marker';
import * as uuid from 'uuid';
import { Rectangle, Dimension } from 'ark-common';

/*
 * This file is referenced from both "book" and "main".
 */

interface MessageDefinition<
  Data,
  ResponseData,
  ResponseType extends string | undefined,
> {
  data: Data; // dummy
  responseData: ResponseData; // dummy
  responseType: ResponseType;
}

function defineMessage<Data, ResponseData = void>() {
  return <ResponseType extends string | undefined = undefined>(
    responseType: ResponseType = undefined as ResponseType,
  ) => {
    type Result = MessageDefinition<Data, ResponseData, ResponseType>;
    return { responseType } as Result;
  };
}

interface OneWayMessage<Type, Data> {
  type: Type;
  data: Data;
}

interface TwoWayMessage<Type, Data> {
  type: Type;
  id: string;
  data: Data;
}

export namespace ContentToViewer {
  export enum MessageType {
    OpenPopup = '--ark-open-popup',
    OpenTab = '--ark-open-tab',
    ClosePopup = '--ark-close-popup',
    CloseTab = '--ark-close-tab',
    SetPopupRect = '--ark-set-popup-rect',
    SelectionChange = '--ark-selection-change',
    PutReflowableContent = '--ark-put-reflowable-content',
    PutFixedLayoutContent = '--ark-put-fixedlayout-content',
    PageStartup = '--ark-page-startup',
    NotifyContentSize = '--ark-notify-content-size',
    PutContentData = '--ark-put-content-data',
    MarkerClicked = '--ark-marker-clicked',
    PostMessage = '--ark-post-message',

    // responses
    EndTemporaryMarker = '--ark-response-end-temporary-marker',
    SelectMarker = '--ark-response-select-marker',
  }

  const messageDefinitionMap = {
    [MessageType.OpenPopup]: defineMessage<OpenPopupMessage>()(),
    [MessageType.OpenTab]: defineMessage<OpenTabMessage>()(),
    [MessageType.ClosePopup]: defineMessage<ClosePopupMessage>()(),
    [MessageType.CloseTab]: defineMessage<CloseTabMessage>()(),
    [MessageType.SetPopupRect]: defineMessage<SetPopupRectMessage>()(),
    [MessageType.SelectionChange]: defineMessage<SelectionMessage>()(),
    [MessageType.PutReflowableContent]: defineMessage<GeneratedContentMessage>()(),
    [MessageType.PutFixedLayoutContent]: defineMessage<GeneratedContentMessage>()(),
    [MessageType.PageStartup]: defineMessage<FrameIDMessage>()(),
    [MessageType.NotifyContentSize]: defineMessage<ContentSizeMessage>()(),
    [MessageType.PutContentData]: defineMessage<KeyValueMessage>()(),
    [MessageType.MarkerClicked]: defineMessage<MarkerMessage>()(),
    [MessageType.PostMessage]: defineMessage<any>()(),

    [MessageType.EndTemporaryMarker]: defineMessage<SelectionMessage>()(),
    [MessageType.SelectMarker]: defineMessage<MarkerMessage>()(),
  };

  export interface MessageDefinitionMap extends Readonly<typeof messageDefinitionMap> {}
  export const MessageDefinitionMap: MessageDefinitionMap = messageDefinitionMap;

  export type Data<T extends MessageType> = MessageDefinitionMap[T]['data'];

  type DataRequired<T extends MessageType> =
    T extends any ? Data<T> extends undefined ? never : T : never;
  type DataNotRequired<T extends MessageType> =
    T extends any ? Data<T> extends undefined ? T : never : never;
  export type MessageWithData = DataRequired<MessageType>;
  export type MessageWithOutData = DataNotRequired<MessageType>;

  export type ResponseType<T extends MessageType> = MessageDefinitionMap[T]['responseType'];
  export type ResponseData<T extends MessageType> = MessageDefinitionMap[T]['responseData'];
  export type Response<T extends MessageType> = ResponseType<T> extends undefined
                                              ? void
                                              : Promise<ResponseData<T>>;

  export type MessageMap = {
    [T in MessageType]: ResponseType<T> extends undefined
                        ? OneWayMessage<T, Data<T>>
                        : TwoWayMessage<T, Data<T>>;
  };

  export type Message<_T extends MessageType = MessageType> = MessageMap[MessageType];

  export function isDomesticMessage(data: any): data is Message {
    return Object.keys(MessageType).some(k => data.type === MessageType[k as any]);
  }

  export type ResponseMessageMap = {
    [T in MessageType]: ResponseType<T> extends undefined
                        ? never
                        : TwoWayMessage<ResponseType<T>, ResponseData<T>>;
  };

  export type ResponseMessage<
    _T extends MessageType = MessageType
  > = ResponseMessageMap[MessageType];

  export function isResponseMessage(data: any): data is ResponseMessage {
    return (Object.keys(MessageDefinitionMap) as MessageType[]).some((k) => {
      return data.type === MessageDefinitionMap[k].responseType;
    });
  }

  export function postMessageToWindow<
    T extends MessageWithOutData, U
  >(target: Window, type: T, data?: Data<T>) : Response<T>;
  export function postMessageToWindow<
    T extends MessageWithData, U
  >(target: Window, type: T, data: Data<T>) : Response<T>;
  export function postMessageToWindow(
    target: Window,
    type: MessageType,
    data?: Data<MessageType>,
  ): Response<MessageType>;
  export function postMessageToWindow<
    T extends MessageType,
  >(target: Window, type: T, data?: Data<T>) {
    if (MessageDefinitionMap[type].responseType !== undefined) {
      const id = uuid.v4();
      target.postMessage({ type, id, data }, '*');
      return new Promise<ResponseData<T>>((resolve) => {
        const handler = (ev: MessageEvent) => {
          const data : {
            type: ResponseType<T>,
            data: ResponseData<T>,
            id: string;
          } = ev.data;
          if (data.type === MessageDefinitionMap[type].responseType && data.id === id) {
            resolve(data.data);
            window.removeEventListener('message', handler);
          }
        };
        window.addEventListener('message', handler, { passive: true });
      });
    }

    const needsBaseURI = type === MessageType.OpenPopup || type === MessageType.OpenTab;

    // 動的生成コンテンツ上でのリンク処理に使うため、baseURIをメッセージに入れる
    if (data !== undefined && needsBaseURI) {
      const base = document.querySelector('base');

      if (base !== null) {
        // <base> が存在する場合はその値を使う
        data.baseURI = base.getAttribute('href');
      } else if ('baseURI' in document) {
        data.baseURI = document.baseURI;
      } else {
        data.baseURI = window.location.href;
      }
    }

    target.postMessage({ type, data }, '*');
  }

  export function postResponseToWindow<T extends MessageWithData>(
    target: Window,
    type: T,
    id: string,
    data?: Data<T>,
  ): void;
  export function postResponseToWindow<T extends MessageWithOutData>(
    target: Window,
    type: T,
    id: string,
    data: Data<T>,
  ): void;
  export function postResponseToWindow(
    target: Window,
    type: MessageType,
    id: string,
    data?: Data<MessageType>,
  ): void;
  export function postResponseToWindow(
    target: Window,
    type: MessageType,
    id: string,
    data?: Data<MessageType>,
  ) {
    target.postMessage({ type, id, data }, '*');
  }

  interface OpenTabMessage {
    path: string;
    key?: string;
    srcPanelID: string;
    active: boolean;
    closeSelf: boolean;
    baseURI: string;
  }

  // type SimpleMessage = undefined;

  interface OpenPopupMessage {
    path: string;
    key: string;
    modal: boolean;
    resizable: boolean;
    titlebar: boolean;
    frame: boolean;
    collapsed: boolean;
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    minWidth?: number;
    minHeight?: number;
    maxWidth?: number;
    maxHeight?: number;
    baseURI: string;
  }

  // keyはタブ内でポップアップを一意に識別するもの。panelIDはビューアが生成するもの。どちらか一方を指定する。
  type ClosePopupMessage = { key: string } | { panelID: string };
  type CloseTabMessage = ClosePopupMessage;

  interface SetPopupRectMessage {
    panelID: string;
    rect: Rectangle;
  }

  interface FrameIDMessage {
    frameID: string;
  }

  interface GeneratedContentMessage {
    key: string;
    content: string;
    title: string;
    intrinsicWidth?: number;
    intrinsicHeight?: number;
    scaling?: 'transform' | 'resize';
  }

  interface ContentSizeMessage {
    panelID: string;
    width: number;
    height: number;
  }

  interface KeyValueMessage {
    key: string;
    value: any;
  }

  export interface SelectionMessage {
    cfi?: string;
    text?: string;
  }

  export interface MarkerMessage {
    id?: string;
  }
}

interface ColorScheme {
  type: string;
  coloringEnabled: boolean;
  userSettingBackgroundColor: {
    red: number,
    green: number,
    blue: number,
    alpha: number,
  };
  userSettingTextColor: {
    red: number,
    green: number,
    blue: number,
    alpha: number,
  };
}

export type ViewerUIMode = 'default' | 'drawing' | 'tts' | 'marker';

export namespace ViewerToContent {

  export enum MessageType {
    SetGeneralRuby = '--ark-set-general-ruby',
    SetFontFamily = '--ark-set-font-family',
    SetFontSize = '--ark-set-font-size',
    SetLineHeight = '--ark-set-line-height',
    SetPadding = '--ark-set-padding',
    SetColorScheme = '--ark-set-color-scheme',
    SetContentFeatureFlags = '--ark-set-content-feature-flags',
    StartTemporaryMarker = '--ark-start-temporary-marker',
    MoveTemporaryMarker = '--ark-move-temporary-marker',
    EndTemporaryMarker = '--ark-end-temporary-marker',
    SelectMarker = '--ark-select-marker',
    GetReflowableContent = '--ark-get-reflowable-content',
    PopupRectChanged = '--ark-popup-rect-changed',
    VisualPropertiesChanged = '--ark-visual-properties-changed',
    NotifyUpdateMarker = '--ark-notify-update-marker',
    NotifyContentDataUpdated = '--ark-notify-update-content-data',
    NotifyVisibilityChange = '--ark-notify-visibility-change',
    NotifyFocusChange = '--ark-notify-focus-change',
    NotifyViewerUIModeChange = '--ark-notify-viewer-ui-mode-change',
    ContentMessage = '--ark-content-message',
  }

  const messageDefinitionMap = {
    [MessageType.SetGeneralRuby]: defineMessage<BooleanMessage>()(),
    [MessageType.SetFontFamily]: defineMessage<StringMessage>()(),
    [MessageType.SetFontSize]: defineMessage<NumberMessage>()(),
    [MessageType.SetLineHeight]: defineMessage<NumberMessage>()(),
    [MessageType.SetPadding]: defineMessage<NumberMessage>()(),
    [MessageType.SetColorScheme]: defineMessage<ColorSchemeMessage>()(),
    [MessageType.SetContentFeatureFlags]: defineMessage<FlagSetMessage>()(),
    [MessageType.PopupRectChanged]: defineMessage<RectangleMessage>()(),
    [MessageType.VisualPropertiesChanged]: defineMessage<VisualPropertiesMessage>()(),
    [MessageType.StartTemporaryMarker]: defineMessage<PointMessage>()(),
    [MessageType.MoveTemporaryMarker]: defineMessage<PointMessage>()(),
    [MessageType.EndTemporaryMarker]:
      defineMessage<PointMessage, ContentToViewer.SelectionMessage>()(
        ContentToViewer.MessageType.EndTemporaryMarker,
      ),
    [MessageType.SelectMarker]: defineMessage<PointMessage, ContentToViewer.MarkerMessage>()(
      ContentToViewer.MessageType.SelectMarker,
    ),
    [MessageType.GetReflowableContent]: defineMessage<SimpleMessage>()(),
    [MessageType.NotifyUpdateMarker]: defineMessage<MarkerMessage>()(),
    [MessageType.NotifyContentDataUpdated]: defineMessage<KeyValueMessage>()(),
    [MessageType.NotifyVisibilityChange] : defineMessage<BooleanMessage>()(),
    [MessageType.NotifyFocusChange] : defineMessage<FocusMessage>()(),
    [MessageType.NotifyViewerUIModeChange] : defineMessage<ViewerUIModeMessage>()(),
    [MessageType.ContentMessage]: defineMessage<any>()(),
  };

  export interface MessageDefinitionMap extends Readonly<typeof messageDefinitionMap> {}
  export const MessageDefinitionMap: MessageDefinitionMap = messageDefinitionMap;

  export type Data<T extends MessageType> = MessageDefinitionMap[T]['data'];

  type DataRequired<T extends MessageType> =
    T extends any ? Data<T> extends undefined ? never : T : never;
  type DataNotRequired<T extends MessageType> =
    T extends any ? Data<T> extends undefined ? T : never : never;
  export type MessageWithData = DataRequired<MessageType>;
  export type MessageWithOutData = DataNotRequired<MessageType>;

  export type ResponseType<T extends MessageType> = MessageDefinitionMap[T]['responseType'];
  export type ResponseData<T extends MessageType> = MessageDefinitionMap[T]['responseData'];
  export type Response<T extends MessageType> = ResponseType<T> extends undefined
                                              ? void
                                              : Promise<ResponseData<T>>;

  export type MessageMap = {
    [T in MessageType]: ResponseType<T> extends undefined
                        ? OneWayMessage<T, Data<T>>
                        : TwoWayMessage<T, Data<T>>;
  };

  export type Message<_T extends MessageType = MessageType> = MessageMap[MessageType];

  export function isDomesticMessage(data: any): data is Message {
    return Object.keys(MessageType).some(k => data.type === MessageType[k as any]);
  }

  export type ResponseMessageMap = {
    [T in MessageType]: ResponseType<T> extends undefined
                        ? never
                        : TwoWayMessage<ResponseType<T>, ResponseData<T>>;
  };

  type ResponseMessage<
    _T extends MessageType = MessageType
  > = ResponseMessageMap[MessageType];

  export function isResponseMessage(data: any): data is ResponseMessage {
    return (Object.keys(MessageDefinitionMap) as MessageType[]).some((k) => {
      return data.type === MessageDefinitionMap[k].responseType;
    });
  }

  export function postMessageToWindow<
    T extends MessageWithOutData, U
  >(target: Window, type: T, data?: Data<T>) : Response<T>;
  export function postMessageToWindow<
    T extends MessageWithData, U
  >(target: Window, type: T, data: Data<T>) : Response<T>;
  export function postMessageToWindow(
    target: Window,
    type: MessageType,
    data?: Data<MessageType>,
  ) : Response<MessageType>;
  export function postMessageToWindow<
    T extends MessageType,
  >(target: Window, type: T, data?: Data<T>) {
    const responseType = MessageDefinitionMap[type].responseType;
    if (responseType !== undefined) {
      const id = uuid.v4();
      target.postMessage({ type, id, data }, '*');
      return new Promise<ResponseData<T>>((resolve) => {
        const handler = (ev: MessageEvent) => {
          if (!isResponseMessage(ev.data)) {
            return;
          }
          const response = ev.data;
          if (response.type === responseType && response.id === id) {
            resolve(response.data);
            window.removeEventListener('message', handler);
          }
        };
        window.addEventListener('message', handler, { passive: true });
      });
    }
    target.postMessage({ type, data }, '*');
  }
  interface SimpleMessage {
    type: MessageType.GetReflowableContent;
  }

  type BooleanMessage = boolean;

  type NumberMessage = number;

  type StringMessage = string;

  type RectangleMessage = Rectangle;

  type ColorSchemeMessage = ColorScheme;

  interface VisualPropertiesMessage {
    visibleRect: Rectangle;       // 表示されている領域(コンテンツiframe内から認識できる座標基準)
    visualScale: number;          // コンテンツiframe自体が視覚的にtransformで拡大されている場合、その倍率
    rootContainerSize: Dimension; // iframeの外側、ビューアの表示領域(tabPanelAreaRect)のサイズ
    popupCollapsed: boolean;      // ポップアップが最小化しているか否か。タブの場合は常にfalse
  }

  interface KeyValueMessage {
    [key: string]: any;
  }

  interface FlagSetMessage {
    [id: string]: boolean;
  }

  type MarkerMessage =  ReadonlyArray<MarkerInfo>;

  export type TemporaryMarkerMessageType = MessageType.StartTemporaryMarker |
    MessageType.MoveTemporaryMarker | MessageType.EndTemporaryMarker;
  export type PointMessage = [number, number];

  export interface FocusMessage {
    focus: boolean;
    activeFrameID: string;
  }

  type ViewerUIModeMessage = ViewerUIMode;
}
